import { TZDateMini, tzName } from "@date-fns/tz";
import { UTCDateMini } from "@date-fns/utc";
import type { TimeZone, WeekDates } from "@qendo/util";
import type { DateTimePickerEvent, EvtTypes } from "@react-native-community/datetimepicker";
import {
    addDays,
    type DateArg,
    differenceInDays,
    endOfDay,
    endOfWeek,
    format,
    formatDistanceToNowStrict,
    getDaysInMonth,
    isAfter,
    isBefore,
    isSameDay,
    isSameMonth,
    isSameYear,
    isThisWeek,
    isThisYear,
    isThursday,
    isToday,
    isTomorrow,
    isTuesday,
    isWeekend,
    isYesterday,
    max,
    min,
    nextFriday,
    nextMonday,
    nextSaturday,
    nextSunday,
    nextThursday,
    nextTuesday,
    nextWednesday,
    parseJSON,
    setDate,
    setDay,
    startOfDay,
    startOfMinute,
    startOfMonth,
    startOfSecond,
    startOfWeek,
    toDate,
} from "date-fns";
import { getCalendars } from "expo-localization";

export const DATE_FORMAT = "yyyy-MM-dd";
export const TIME_FORMAT = "HH:mm:ss";
export const TIME_FORMAT_WITHOUT_SECONDS = "HH:mm:00";
export const HUMAN_DAY = "do";
export const HUMAN_SHORTEST_DATE = "d MMM";
export const HUMAN_SHORT_NEAR_DATE = "MMM do";
export const HUMAN_NEAR_DATE = "MMMM do";
export const HUMAN_SHORT_DATE = "d MMMM";
export const HUMAN_MEDIUM_DATE = "d MMM yyyy";
export const HUMAN_LONG_DATE = "d MMMM yyyy";
export const HUMAN_LONGER_DATE = "MMMM do, yyyy";
export const HUMAN_TIME = "h:mm aaa";
export const MONTH_NAME = "MMMM";
export const MONTH_YEAR_NAME = "MMMM yyyy";
export const YEAR_NAME = "yyyy";
export const DAY_INITIAL = "iiiii";
export const DAY_EXTENDED_INITIAL = "iiiiii";

export function formatDateString(from: DateArg<Date>): DateArg<Date> & string {
    return format(toDate(from), DATE_FORMAT);
}
export function formatTimeString(from: DateArg<Date>): DateArg<Date> & string {
    return format(parseTime(from), TIME_FORMAT);
}

export function formatDateTimeString(from: DateArg<Date>): DateArg<Date> & string {
    return format(toDate(from), `${DATE_FORMAT} ${TIME_FORMAT}`);
}

export function getMiddleOfWeek(from?: DateArg<Date> | null | undefined): DateArg<Date> & string {
    return formatDateString(setDay(toDate(from ?? new Date()), 3));
}

export function getMiddleOfMonth(from?: DateArg<Date> | null | undefined): DateArg<Date> & string {
    const targetDay = toDate(from ?? new Date());
    const days = getDaysInMonth(targetDay);
    const middle = Math.floor(days / 2);

    return formatDateString(setDate(targetDay, middle));
}

export function getMonth(from?: DateArg<Date> | null | undefined): DateArg<Date> & string {
    return formatDateString(startOfMonth(toDate(from ?? new Date())));
}
export function getMonthsForWeek(from?: DateArg<Date> | null | undefined): (DateArg<Date> & string)[] {
    const targetDay = toDate(from ?? new Date());
    const start = getMonth(startOfWeek(targetDay));
    const end = getMonth(endOfWeek(targetDay));

    return isSameMonth(start, end) ? [start] : [start, end];
}
export function getWeek(from?: DateArg<Date> | null | undefined): DateArg<Date> & string {
    return formatDateString(startOfWeek(toDate(from ?? new Date())));
}

export function formatTimeStartOfSecond(from: DateArg<Date>): string {
    return formatTimeString(startOfSecond(parseTime(from)));
}

export function formatDateTimeStartOfSecond(from: DateArg<Date>): string {
    return formatDateTimeString(startOfSecond(toDate(from)));
}

export function formatDateTimeStartOfMinute(from: DateArg<Date>): string {
    return formatDateTimeString(startOfMinute(toDate(from)));
}

export function formatTimeForHumans(from: DateArg<Date>): string {
    return format(parseTime(from), HUMAN_TIME);
}

export type DateMode =
    | "approximate"
    | "basic"
    | "locale"
    | "near"
    | "short"
    | "shortest"
    | "medium"
    | "long"
    | "longer";

export function formatLocaleDateForHumans(from: DateArg<Date>): string {
    return toDate(from).toLocaleDateString();
}

export function formatNearDateForHumans(from: DateArg<Date>, short?: boolean): string {
    return format(toDate(from), short ? HUMAN_SHORT_NEAR_DATE : HUMAN_NEAR_DATE);
}

export function formatShortestDateForHumans(from: DateArg<Date>): string {
    return format(toDate(from), HUMAN_SHORTEST_DATE);
}

export function formatMonthDayForHumans(from: DateArg<Date>): string {
    return format(toDate(from), HUMAN_DAY);
}

export function formatShortDateForHumans(from: DateArg<Date>): string {
    return format(toDate(from), HUMAN_SHORT_DATE);
}

export function formatMediumDateForHumans(from: DateArg<Date>): string {
    return format(toDate(from), HUMAN_MEDIUM_DATE);
}

export function formatLongDateForHumans(from: DateArg<Date>): string {
    return format(toDate(from), HUMAN_LONG_DATE);
}

export function formatLongerDateForHumans(from: DateArg<Date>): string {
    return format(toDate(from), HUMAN_LONGER_DATE);
}

export function formatDateRangeForHumans(start: DateArg<Date>, end: DateArg<Date>): string {
    const start_at = toDate(start);
    const end_at = toDate(end);

    if (isSameDay(start_at, end_at)) {
        return formatShortDateForHumans(start_at);
    } else if (isSameYear(start_at, end_at)) {
        return `${format(start_at, HUMAN_SHORTEST_DATE)} - ${format(end_at, HUMAN_MEDIUM_DATE)}`;
    }

    return `${format(start_at, HUMAN_MEDIUM_DATE)} - ${format(end_at, HUMAN_MEDIUM_DATE)}`;
}

export function formatFuzzyTimeForHumans(from: DateArg<Date>, addSuffix?: boolean, asUtc?: boolean): string {
    const target = asUtc && typeof from === "string" ? parseJSON(from) : toDate(from);

    return formatDistanceToNowStrict(target, { addSuffix });
}

export function formatApproximateDistanceForHumans(from: DateArg<Date>, addSuffix?: boolean, asUtc?: boolean): string {
    const target = asUtc && typeof from === "string" ? parseJSON(from) : toDate(from);

    return (
        (isToday(target) && "Today") ||
        (isTomorrow(target) && "Tomorrow") ||
        (isYesterday(target) && "Yesterday") ||
        formatFuzzyTimeForHumans(target, addSuffix)
    );
}

export function formatApproximateDateForHumans(from: DateArg<Date>, short?: boolean): string {
    const target = toDate(from);

    return (
        (isToday(target) && "Today") ||
        (isTomorrow(target) && "Tomorrow") ||
        (isYesterday(target) && "Yesterday") ||
        (isThisWeek(target) && format(target, "EEEE")) ||
        (isThisYear(target) && formatNearDateForHumans(target, short)) ||
        (short && formatShortDateForHumans(target)) ||
        formatLongDateForHumans(target)
    );
}

export function formatShortMonthName(from?: DateArg<Date> | null | undefined): string {
    return format(toDate(from ?? new Date()), "MMM");
}

export function formatMonthName(from?: DateArg<Date> | null | undefined, with_year?: boolean): string {
    const target = toDate(from ?? new Date());

    return format(target, (with_year ?? !isThisYear(target)) ? MONTH_YEAR_NAME : MONTH_NAME);
}

export function formatYearName(from?: DateArg<Date> | null | undefined): string {
    const target = toDate(from ?? new Date());

    return format(target, YEAR_NAME);
}

export function formatWeek(from?: DateArg<Date> | null | undefined): string {
    const target = toDate(from ?? new Date());

    return formatDateRangeForHumans(startOfWeek(target), endOfWeek(target));
}

export function formatDateForHumans(from: DateArg<Date>, mode?: DateMode, short?: boolean): string {
    return {
        approximate: formatApproximateDateForHumans,
        basic: formatDateString,
        locale: formatLocaleDateForHumans,
        near: formatNearDateForHumans,
        short: formatShortDateForHumans,
        shortest: formatShortestDateForHumans,
        medium: formatMediumDateForHumans,
        long: formatLongDateForHumans,
        longer: formatLongerDateForHumans,
    }[mode ?? "long"](from, short);
}

export function formatDateTimeForHumans(from: DateArg<Date>, dateMode?: DateMode): string {
    const datePart = formatDateForHumans(from, dateMode);
    const timePart = formatTimeForHumans(from);

    return `${datePart}, ${timePart}`;
}

export function formatDayOfWeekInitial(from: DateArg<Date>): string {
    const target = toDate(from);

    return format(
        target,
        isWeekend(target) || isTuesday(target) || isThursday(target) ? DAY_EXTENDED_INITIAL : DAY_INITIAL,
    );
}

export function daysForPeriod(start: DateArg<Date>, end: DateArg<Date>) {
    const [start_at, end_at] = isSameDayOrLater(start, end)
        ? [startOfDay(end), endOfDay(start)]
        : [startOfDay(start), endOfDay(end)];

    const diff = differenceInDays(end_at, start_at);

    if (diff <= 0) {
        return [start_at];
    }

    return [...Array.from({ length: diff }).keys()].map((idx) => addDays(start_at, idx));
}

export function daysForWeek(target: DateArg<Date>) {
    const start = startOfWeek(target);

    return [...Array.from({ length: 7 }).keys()].map((idx) => addDays(start, idx));
}

export function daysForMonth(target: DateArg<Date>) {
    const start = startOfMonth(target);
    const days = getDaysInMonth(start);

    return [...Array.from({ length: days }).keys()].map((idx) => addDays(start, idx));
}

export function isSameDayOrLater(date: DateArg<Date>, dateToCompare: DateArg<Date>): boolean {
    return isSameDay(date, dateToCompare) || isAfter(date, dateToCompare);
}
export function isSameDayOrEarlier(date: DateArg<Date>, dateToCompare: DateArg<Date>): boolean {
    return isSameDay(date, dateToCompare) || isBefore(date, dateToCompare);
}

export function datesBetween(start: DateArg<Date>, end?: DateArg<Date> | null): Date[] {
    const first = end ? min([start, end]) : start;
    const last = end ? max([start, end]) : start;

    const length = differenceInDays(last, first) + 1;

    return [...Array.from({ length }).keys()].map((idx) => addDays(first, idx));
}

export function parseTime(time: DateArg<Date>): Date {
    const formatted =
        typeof time === "string"
            ? time.replace(
                  /^([0-2][0-9]:[0-5][0-9])(:[0-5][0-9]\.?[0-9]{0,6})?$/,
                  `${formatDateString(new Date())} $1:00`,
              )
            : time;

    return toDate(formatted);
}

export function extractTime(time: DateArg<Date>) {
    const parsed = parseTime(time);

    return {
        hour: parsed.getHours(),
        minute: parsed.getMinutes(),
        second: parsed.getSeconds(),
    };
}

export function getWeekFromDate(date?: DateArg<Date>): WeekDates {
    const start = toDate(date ?? new Date());

    return {
        monday: nextMonday(start),
        tuesday: nextTuesday(start),
        wednesday: nextWednesday(start),
        thursday: nextThursday(start),
        friday: nextFriday(start),
        saturday: nextSaturday(start),
        sunday: nextSunday(start),
    };
}

export function extractDate(date: DateArg<Date>) {
    const parsed = toDate(date);

    return {
        year: parsed.getFullYear(),
        month: parsed.getMonth(),
        day: parsed.getDate(),
        weekday: parsed.getDay(),
        week: getWeek(parsed),
    };
}

export type WeekdayMode = "initial" | "short" | "full";

export function isAmbiguousWeekday(date: DateArg<Date>): boolean {
    return isWeekend(date) || isThursday(date) || isTuesday(date);
}

export function formatWeekDay(date: DateArg<Date>, mode: WeekdayMode = "short"): string {
    const parsed = toDate(date);

    switch (mode) {
        case "full":
            return format(parsed, "cccc");
        case "initial":
            return format(parsed, isAmbiguousWeekday(parsed) ? "cccccc" : "ccccc");
        default:
            return format(parsed, "ccc");
    }
}

export type Mode = "date" | "time" | "datetime";

export function fromUtc(date: DateArg<Date>): Date {
    return new UTCDateMini(typeof date === "string" ? UTCDateMini.parse(date as DateArg<Date> & string) : date);
}

export function fromTimezone(date: DateArg<Date>, time_zone?: string | null): Date {
    return new TZDateMini(date as string, time_zone ?? currentTimezone());
}

export function getOffset(time_zone?: string | null): number {
    return new TZDateMini(new Date(), time_zone ?? currentTimezone()).getTimezoneOffset();
}

export function currentTimezone(): TimeZone {
    return (getCalendars()[0].timeZone ?? Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone) as TimeZone;
}

export function extractTimezoneRegion(time_zone: TimeZone): string {
    return time_zone.substring(0, time_zone.indexOf("/"));
}

export function sortTimeZones(zones: TimeZone[], current: TimeZone): TimeZone[] {
    const region = extractTimezoneRegion(current);

    return zones.sort(
        (a, b) =>
            ((a === current || (a.startsWith(region) && !b.startsWith(region))) && -1) ||
            ((b === current || (!a.startsWith(region) && b.startsWith(region))) && 1) ||
            (a < b ? -1 : 1),
    );
}

export function mergeDateTime(date: DateArg<Date>, time: DateArg<Date>): Date {
    return toDate(`${formatDateString(date)} ${formatTimeString(time)}`);
}

export function formatTimezoneName(time_zone: TimeZone): string {
    return time_zone.substring(time_zone.lastIndexOf("/") + 1).replace("_", " ");
}

export function timezonesMatch(a: TimeZone, b: TimeZone): boolean {
    return a === b || getOffset(a) === getOffset(b);
}

export function formatTimezoneDetails(time_zone: string, current?: TimeZone, at?: DateArg<Date> | null): string {
    const region = extractTimezoneRegion(current ?? currentTimezone());

    const short_name = tzName(time_zone, toDate(at ?? new Date()), "long");
    const locale = time_zone
        .replace(`${region}/`, "")
        .replace(/\/?[^/]+$/, "")
        .replace("/", " > ")
        .replace("_", " ")
        .trim();

    return `${short_name} ${locale}`.trim();
}

export function parseInput(from: DateArg<Date>, mode: Mode): Date {
    return mode === "time" ? parseTime(from) : toDate(from);
}

export function clampDate(date: DateArg<Date>, minimum?: DateArg<Date> | null, maximum?: DateArg<Date> | null) {
    const value = toDate(date);
    const top = maximum ? min([toDate(maximum), value]) : value;

    return minimum ? max([toDate(minimum), top]) : top;
}

export function makePickerEvent(type: EvtTypes, date: Date): DateTimePickerEvent {
    return {
        type,
        nativeEvent: {
            timestamp: date.getTime(),
            utcOffset: date.getTimezoneOffset(),
        },
    };
}
